/**
 * `ICustomerVectorStoreModel` — port of Unity's
 * `Models/StorageModel/CustomerVectorStoreModel.cs`, the harness's **Memory**
 * layer.
 *
 * One OpenAI vector store per Campfire CUSTOMER, named with the customer id
 * and carrying `metadata.customer_id`. Its id is persisted at the
 * customer-scoped storage path `vectorstore/vector_store.json`, so every
 * device — Unity desktop and this web client — indexes into and searches the
 * SAME store.
 *
 * Documents are customer-wide; the product scoping is a retrieval-time FILTER
 * on the attributes written at attach time, not a separate store.
 *
 * Per `Campfire_AI_Harness.md`, this is the only seam allowed to speak the
 * vector-store / file APIs: context, library, and asset callers must route
 * through it rather than assembling `file_search` payloads themselves.
 */

/** The `file_search` tool block for a Responses API request. */
export type FileSearchTool = Record<string, unknown>;

export interface ProductDocumentVectorSource {
  readonly storagePath: string;
  readonly fileName: string;
  readonly fileSize: number;
  readonly lastModifiedMs: number;
}

export interface WorkflowDocumentVectorSource extends ProductDocumentVectorSource {
  readonly knowledgeKey: string;
}

export interface ProductDocumentVectorSyncResult {
  readonly skipped: boolean;
  readonly reason?: string;
  readonly checked: number;
  readonly current: number;
  readonly uploaded: number;
  readonly deleted: number;
  readonly failed: number;
}

/**
 * The RETRIEVAL floor: sent to `file_search` as
 * `ranking_options.score_threshold`, so the store drops everything below it
 * SERVER-SIDE and the knowledge sub-agent never reads it.
 *
 * This is the lower of two floors and they do different jobs. This one decides
 * what the model may READ; `K_MIN_RETRIEVED_SOURCE_SCORE` in
 * `AIContextEngineModel.KnowledgeTransport.ts` decides which merely-matched
 * document is DISPLAYED to the user as a source, and is deliberately higher —
 * see the note on that constant for why the display floor must be the stricter
 * of the pair.
 *
 * WHY IT EXISTS: without it `file_search` returns its `max_num_results`
 * NEAREST chunks however badly they match, so a question the documents do not
 * answer still hands the sub-agent six plausible passages about the right
 * subject, with no scores in its context to mark them as noise. "Nothing
 * relevant" then has to be a judgement the model makes while holding adjacent
 * text — which is the near-miss shape that produced the offer-the-neighbour and
 * relabel-the-neighbour failures recorded on 2026-08-27.
 *
 * VALUE: 0.5 is the inclusive end of the range in production use for this
 * parameter (0.5-0.6 recall-oriented, 0.7-0.8 precision-oriented); OpenAI
 * publishes no default and applies none. It is also 2.5x the top of the noise
 * band measured against the real customer store (0.10-0.20) and 0.31 below the
 * bottom of the measured hit band (0.81-0.97), so it sits mid-empty-band with
 * headroom both ways.
 *
 * NOTE the scale. This is a NORMALIZED RERANKER score in [0, 1], not cosine
 * similarity — the familiar 0.75-0.8 cosine convention does not transfer and
 * importing it would cut genuine hits. Azure AI Search's reranker is a 0-4
 * scale for the same reason: do not port a cutoff across scales.
 *
 * A SERVER-SIDE FLOOR IS BLIND: filtered chunks never come back, so nothing can
 * log what was dropped and over-refusal is the hard failure to diagnose. The
 * calibration behind these numbers is ONE question against a TWO-document
 * project. Re-tune against real traffic by temporarily passing 0 here and
 * logging the score distribution.
 */
export const K_RETRIEVAL_SCORE_FLOOR = 0.5;

export interface ICustomerVectorStoreModel {
  /**
   * Returns the customer's vector-store id, creating and persisting it if
   * needed, or `null` when AI is disabled / unauthenticated / unreachable.
   *
   * Unity's resolution order (`:120-189`), ported exactly: the persisted id is
   * read, but a LIST scan for the most recent store named `customerId` always
   * runs and wins — that is what keeps a stale persisted id from pinning the
   * client to a deleted store. Only when the scan finds nothing is the
   * persisted id verified and reused; only then is a new store created.
   */
  getOrCreateVectorStoreIdAsync(
    customerId: string,
    signal?: AbortSignal,
  ): Promise<string | null>;

  /**
   * The workflow-product-scoped `file_search` tool payload for a Responses request, or
   * `null` when there is no usable store.
   *
   * Filter shape: `product` equals the **normalized** workflow product key
   * (`normalizeProductNameKey`), optionally `and`-ed with an `eq` on
   * `filename` to restrict to one document. OpenAI `eq` is case-sensitive.
   */
  tryBuildProductFileSearchToolAsync(
    customerId: string,
    productKey: string,
    restrictToFileName?: string | null,
    maxNumResults?: number,
    signal?: AbortSignal,
  ): Promise<FileSearchTool | null>;

  /**
   * The workflow knowledge `file_search` payload. Product documents are matched
   * by `product` only when there is no workflow id. Once a workflow is loaded,
   * the workflow id is the retrieval scope, so older product-wide attachments
   * cannot leak into a workflow-scoped answer.
   */
  tryBuildWorkflowKnowledgeFileSearchToolAsync(
    customerId: string,
    args: {
      readonly productKey?: string | null;
      readonly workflowId?: string | null;
      readonly restrictToFileName?: string | null;
      readonly maxNumResults?: number;
    },
    signal?: AbortSignal,
  ): Promise<FileSearchTool | null>;

  /**
   * Uploads a document and attaches it to the customer's store, tagged with
   * the workflow product so retrieval can filter to it. Resolves `true` only once the
   * file has finished indexing (Unity polls to completion so a caller never
   * reports success for a doc that is not yet searchable).
   *
   * Web signature deviation: Unity takes a local file PATH; a browser has a
   * `File`, so the bytes are passed directly.
   */
  syncProductDocumentUploadAsync(
    customerId: string,
    productKey: string,
    bytes: Uint8Array,
    fileName: string,
    signal?: AbortSignal,
  ): Promise<boolean>;

  /**
   * Ensures every visible product document is attached to the customer vector
   * store with current source-version attributes. This is retained for product
   * document maintenance paths; question-time retrieval must not call it.
   */
  syncProductDocumentsAsync(
    customerId: string,
    productKey: string,
    documents: ReadonlyArray<ProductDocumentVectorSource>,
    signal?: AbortSignal,
  ): Promise<ProductDocumentVectorSyncResult>;

  /**
   * Ensures direct `workflow.knowledge.include` documents are attached to the
   * customer vector store under the workflow scope. Removing a key from the
   * workflow deletes only files tracked for that workflow id; it does not list
   * or reconcile the whole customer vector store.
   */
  syncWorkflowDocumentsAsync(
    customerId: string,
    workflowId: string,
    documents: ReadonlyArray<WorkflowDocumentVectorSource>,
    signal?: AbortSignal,
  ): Promise<ProductDocumentVectorSyncResult>;

  /**
   * Removes a document's vector-store files tagged with this workflow product.
   *
   * Resolves `true` when nothing matched (Unity `:409`) — a doc that was never
   * indexed must not block its own deletion.
   */
  syncProductDocumentDeletionAsync(
    customerId: string,
    productKey: string,
    storagePath: string,
    fileName: string,
    signal?: AbortSignal,
  ): Promise<boolean>;

  /** Drop the cached store id (sign-out / account switch). */
  reset(): void;
}

export const CUSTOMER_VECTOR_STORE_MODEL = Symbol.for("Campfire.CustomerVectorStoreModel");
